<!--
  Constructor de páginas del informe (NUEVO, prototipo portado del artifact).
  3ª pestaña de report-manage.vue. Solo frontend / maqueta, SIN persistencia todavía.
  - Estado y formularios: Vue idiomático (v-model).
  - Vista previa A4: HTML generado por funciones puras (data/page-builder.render.js) e
    inyectado con v-html. El CSS (namespace `.rpb`) vive en
    resources/assets/sass/front/components/epc-report-builder.scss (reutilizable; también
    lo usa el preview de Plantillas), importado global en app.scss.
-->
<template>
  <div class="rpb">
    <!-- Barra superior: nombre/tipo de la página + conmutador de vista + ⚙ parámetros -->
    <div class="pv-bar">
      <span class="pv-name">{{ selected ? displayName(selected) : '—' }}</span>
      <span class="pv-ty" v-if="selected">{{ typeLabel(selected.type) }}</span>
      <span style="color:var(--faint);font-size:11px">{{ viewMode === 'full' ? $t('full_document') : (selected && !selected.visible ? '(' + $t('page_hidden') + ')' : '') }}</span>
      <span v-if="activeTemplate" class="rpb-tpl">{{ $t('template') }}: {{ activeTemplate.name }}<span v-if="activeTemplate.modified" class="rpb-tpl-mod"> · {{ $t('modified') }}</span></span>
      <div class="seg" style="margin-left:auto">
        <button :aria-pressed="viewMode === 'page'" @click="viewMode = 'page'">{{ $t('this_page') }}</button>
        <button :aria-pressed="viewMode === 'full'" @click="viewMode = 'full'">{{ $t('full_document') }}</button>
      </div>
      <b-button size="is-small" icon-pack="fas" icon-left="arrow-left" style="margin-left:10px" @click="$emit('back')">{{ $t('back') }}</b-button>
    </div>

    <div class="rpb-grid">
      <!-- Zona 1: páginas (rail) -->
      <div class="zone-pages">
        <p class="zone-h">{{ $t('pages') }} <span class="count">{{ pages.length }}</span></p>
        <div class="pagelist">
          <div
            v-for="(p, i) in pages"
            :key="p.id"
            class="pcard"
            :class="{ active: p.id === selectedId, 'hidden-pg': !p.visible }"
            @click="selectPage(p.id)">
            <div class="top">
              <span class="num">{{ i + 1 }}</span>
              <span class="pname">{{ displayName(p) }}</span>
              <span class="acts" @click.stop>
                <b-button size="is-small" type="is-text" icon-pack="fas" icon-left="chevron-up" :disabled="i === 0" :title="$t('move_up')" @click="move(p.id, -1)" />
                <b-button size="is-small" type="is-text" icon-pack="fas" icon-left="chevron-down" :disabled="i === pages.length - 1" :title="$t('move_down')" @click="move(p.id, 1)" />
                <b-button size="is-small" type="is-text" icon-pack="fas" :icon-left="p.visible ? 'eye' : 'eye-slash'" :title="p.visible ? $t('hide') : $t('show')" @click="toggleVis(p.id)" />
                <b-button size="is-small" type="is-text" icon-pack="fas" icon-left="trash" class="has-text-danger" :disabled="pages.length === 1" :title="$t('remove')" @click="removePage(p.id)" />
              </span>
            </div>
            <span class="ptype" :class="TYPES[p.type].custom">{{ TYPES[p.type].custom === 'admin' ? $t('admin') : $t('user') }}</span>
          </div>
        </div>

        <b-button type="is-primary" icon-pack="fas" icon-left="plus" expanded class="mt-4" @click="paletteOpen = !paletteOpen">{{ $t('add_page') }}</b-button>
        <div v-if="paletteOpen" class="palette">
          <button
            v-for="it in paletteItems"
            :key="it.key"
            :disabled="it.disabled"
            :class="{ 'opacity-50 cursor-not-allowed': it.disabled }"
            @click="addPage(it.key)">
            <b-icon pack="fas" :icon="it.icon" size="is-small" />
            <span class="flex-1">{{ it.name }}</span>
            <b-icon v-if="it.disabled" pack="fas" icon="check" size="is-small" />
          </button>
        </div>
      </div>

      <!-- Zona 2: vista previa A4 (el botón de parámetros vive aquí, centrado) -->
      <div class="zone-preview relative flex flex-col items-center min-w-0">
        <div class="pv-card w-full">
          <div class="rpb-preview" v-html="previewHtml"></div>
        </div>
        <b-button
          v-if="selected && !drawerOpen"
          type="is-primary"
          icon-pack="fas"
          icon-left="cog"
          size="is-small"
          rounded
          class="sticky bottom-5 z-30 mt-4 shadow-lg"
          @click="openDrawer">{{ $t('parameters') }}</b-button>
      </div>
    </div>

    <!-- Drawer de parámetros (desde la derecha) -->
    <div class="drawer-backdrop" :class="{ open: drawerOpen }" @click="closeDrawer"></div>
    <aside class="drawer" :class="{ open: drawerOpen }" :aria-hidden="drawerOpen ? 'false' : 'true'">
      <div class="drawer-head" v-if="selected">
        <div style="flex:1;min-width:0">
          <div class="dt">{{ displayName(selected) }}</div>
          <div class="ds">{{ typeDescLabel(selected.type) }}</div>
        </div>
        <span class="custom" :class="TYPES[selected.type].custom">{{ TYPES[selected.type].custom === 'admin' ? $t('admin') : $t('user') }}</span>
        <b-button type="is-text" icon-pack="fas" icon-left="times" size="is-small" @click="closeDrawer" />
      </div>

      <!-- Estado de autoguardado: se muestra al guardar y desaparece al terminar; el error queda con ✕ -->
      <div v-if="selected && saveState" class="drawer-status" :class="saveState">
        <b-icon pack="fas" :icon="saveIcon" :custom-class="saveState === 'saving' ? 'fa-spin' : ''" size="is-small" />
        <span>{{ saveLabel }}</span>
        <span v-if="saveState === 'error'" class="status-close" title="Cerrar" @click="dismissSave"><b-icon pack="fas" icon="times" size="is-small" /></span>
      </div>

      <div class="drawer-body" v-if="selected">
        <!-- PORTADA -->
        <template v-if="selected.type === 'cover'">
          <div class="field"><label class="lbl">{{ $t('title') }}</label><b-input v-model="s.title" :placeholder="project.company.toUpperCase()" /></div>
          <div class="field"><label class="lbl">{{ $t('subtitle') }}</label><b-input v-model="s.subtitle" :placeholder="$t('report_subtitle_placeholder')" /></div>
          <div class="field"><label class="lbl">{{ $t('description') }}</label><b-input type="textarea" v-model="s.description" :placeholder="$t('report_description_placeholder')" /></div>
          <div class="field">
            <label class="lbl">{{ $t('cover_image') }}</label>
            <div class="seg" style="margin-bottom:8px">
              <button :aria-pressed="s.image_cover.type === 'file'" @click="setCoverType('file')">{{ $t('upload_file') }}</button>
              <button :aria-pressed="s.image_cover.type === 'url'" @click="setCoverType('url')">URL</button>
            </div>
            <input v-if="s.image_cover.type === 'url'" type="text" v-model="s.image_cover.url" placeholder="https://…">
            <template v-else>
              <label class="dropzone">
                <img v-if="coverSrc" :src="coverSrc" alt="">
                <template v-else>⬆ {{ $t('drag_or_click_to_upload') }}</template>
                <input type="file" accept="image/jpg,image/jpeg,image/png" style="display:none" @change="onCoverFile">
              </label>
              <b-button v-if="coverSrc" type="is-text" size="is-small" icon-pack="fas" icon-left="trash" class="has-text-danger mt-2" @click="deleteCover">{{ $t('remove_image') }}</b-button>
            </template>
          </div>
        </template>

        <!-- DASHBOARD -->
        <template v-else-if="selected.type === 'dashboard'">
          <div class="field">
            <label class="lbl">{{ $t('group_progress') }}</label>
            <div class="seg">
              <button :aria-pressed="s.showGroupProgress === 'parent'" @click="setSeg('showGroupProgress', 'parent')">{{ $t('parent') }}</button>
              <button :aria-pressed="s.showGroupProgress === 'children'" @click="setSeg('showGroupProgress', 'children')">{{ $t('children') }}</button>
              <button :aria-pressed="s.showGroupProgress === 'custom'" @click="setSeg('showGroupProgress', 'custom')">{{ $t('custom') }}</button>
            </div>
            <div class="hint">{{ $t('custom_mode_groups_hint') }}</div>
          </div>
          <div class="field" v-if="s.showGroupProgress === 'custom'">
            <label class="lbl">{{ $t('groups') }}</label>
            <treeselect
              v-model="s.customGroups"
              :multiple="true"
              :options="activityGroups"
              :normalizer="treeNormalizer"
              :placeholder="$t('select_groups')"
              append-to-body />
            <div class="hint">{{ $t('only_selected_groups_shown') }}</div>
          </div>
          <div class="field">
            <label class="lbl">{{ $t('first_date') }}</label>
            <div class="seg">
              <button v-for="d in DATE_TYPES" :key="d[0]" :aria-pressed="s.firstDatePairType === d[0]" @click="setDatePair('firstDatePairType', d[0])">{{ d[1] }}</button>
            </div>
          </div>
          <div class="field">
            <label class="lbl">{{ $t('second_date') }}</label>
            <div class="seg">
              <button v-for="d in DATE_TYPES" :key="d[0]" :disabled="d[0] === s.firstDatePairType" :aria-pressed="s.secondDatePairType === d[0]" @click="setDatePair('secondDatePairType', d[0])">{{ d[1] }}</button>
            </div>
          </div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('current_progress_chart') }}</label><b-switch size="is-small" v-model="s.showChart" /></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('general_progress_chart') }}</label><b-switch size="is-small" v-model="s.showGeneral" /></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('table') }}</label><b-switch size="is-small" v-model="s.showTable" /></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('show_end_date_on_completion') }}</label><b-switch size="is-small" v-model="s.showEndDateOnCompletion" /></div><div class="hint">{{ $t('show_end_date_hint') }}</div></div>
        </template>

        <!-- RESUMEN EJECUTIVO -->
        <template v-else-if="selected.type === 'exec'">
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('overall_progress') }}</label><b-switch size="is-small" v-model="s.showOverall" /></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('breakdown_by_group') }}</label><b-switch size="is-small" v-model="s.showByGroup" /></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('milestones') }}</label><b-switch size="is-small" v-model="s.showMilestones" /></div></div>
        </template>

        <!-- GRÁFICOS POR GRUPO -->
        <template v-else-if="selected.type === 'groupcharts'">
          <div class="rpb-note">
            <b-icon pack="fas" icon="circle-info" size="is-small" />
            <span>{{ $t('group_charts_note') }}</span>
          </div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">Scheduled progress</label><b-switch size="is-small" v-model="s.scheduled" /></div><div class="hint">{{ $t('scurve_line_scheduled') }}</div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">Current progress</label><b-switch size="is-small" v-model="s.current" /></div><div class="hint">{{ $t('scurve_line_current') }}</div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('last_planned') }}</label><b-switch size="is-small" v-model="s.lastPlanned" /></div><div class="hint">{{ $t('scurve_line_last_planned') }}</div></div>
        </template>

        <!-- CURVA S -->
        <template v-else-if="selected.type === 'scurve'">
          <div class="rpb-note">
            <b-icon pack="fas" icon="circle-info" size="is-small" />
            <span>{{ $t('scurve_note') }}</span>
          </div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('dashboard_group_curves') }}</label><b-switch size="is-small" v-model="scInclude" :disabled="groupchartsExists" /></div><div class="hint">{{ groupchartsExists ? $t('scurve_dashboard_curves_exists_hint') : $t('scurve_dashboard_curves_off_hint') }}</div></div>
          <div class="field" v-if="scInclude && dashboardGroupNames.length">
            <label class="lbl">{{ $t('dashboard_groups_fixed') }}</label>
            <div class="chips">
              <span v-for="n in dashboardGroupNames" :key="n" class="chip-ro">{{ n }}</span>
            </div>
          </div>
          <div class="field">
            <label class="lbl">{{ $t('add_extra_groups') }}</label>
            <treeselect
              v-model="s.extraGroups"
              :multiple="true"
              :flat="true"
              :options="activityGroups"
              :normalizer="scurveNormalizer"
              :placeholder="$t('add_extra_groups')"
              append-to-body />
            <div class="hint" v-if="scInclude">{{ $t('scurve_extra_groups_hint') }}</div>
          </div>
          <div v-if="!scurveHasGroups" class="rpb-note" style="background:var(--surface-2);color:var(--muted);border-color:var(--line)">
            <b-icon pack="fas" icon="circle-info" size="is-small" />
            <span>{{ $t('scurve_select_group_hint') }}</span>
          </div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('bars_chart_top') }}</label><b-switch size="is-small" v-model="s.showBars" :disabled="!scurveHasGroups" /></div><div class="hint">{{ $t('bars_chart_top_hint') }}</div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">Scheduled progress</label><b-switch size="is-small" v-model="s.scheduled" :disabled="!scurveHasGroups" /></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">Current progress</label><b-switch size="is-small" v-model="s.current" :disabled="!scurveHasGroups" /></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('last_planned') }}</label><b-switch size="is-small" v-model="s.lastPlanned" :disabled="!scurveHasGroups" /></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('show_legend') }}</label><b-switch size="is-small" v-model="s.legend" :disabled="!scurveHasGroups" /></div></div>
        </template>

        <!-- IMÁGENES -->
        <template v-else-if="selected.type === 'images'">
          <div class="field"><label class="lbl">{{ $t('columns') }}</label><div class="seg"><button v-for="c in [1,2,3]" :key="c" :aria-pressed="s.columns === c" @click="setSeg('columns', c)">{{ c }}</button></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('number_of_images') }}</label><span class="stepper"><button @click="step('numberOfExtraImages', -1)">−</button><input type="number" v-model.number="s.numberOfExtraImages" min="0"><button @click="step('numberOfExtraImages', 1)">＋</button></span></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('show_caption') }}</label><b-switch size="is-small" v-model="s.showCaption" /></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('user_defines_legend') }}</label><b-switch size="is-small" v-model="s.userCanDefineLegend" /></div></div>
        </template>

        <!-- DISCIPLINA -->
        <template v-else-if="selected.type === 'discipline'">
          <div class="field"><label class="lbl">{{ $t('discipline_code') }}</label><select v-model="s.discipline"><option v-for="d in disciplineOptions(selected)" :key="d[0]" :value="d[0]">{{ d[0] }} · {{ d[1] }}</option></select><div class="hint">{{ $t('discipline_code_hint') }}</div></div>
          <div class="field"><label class="lbl">{{ $t('section_name') }}</label><input type="text" v-model="s.name" :placeholder="discName(s.discipline)"></div>
          <div class="field">
            <label class="lbl">{{ $t('related_group') }}
              <span v-if="disciplineLinkBadge" class="link-badge" :class="disciplineLinkBadge.cls">{{ disciplineLinkBadge.txt }}</span>
            </label>
            <treeselect
              v-model="s.groupId"
              :options="activityGroups"
              :normalizer="disciplineGroupNormalizer"
              :placeholder="$t('select_associated_group')"
              append-to-body />
            <div class="hint">{{ $t('discipline_group_hint') }}</div>
          </div>
          <div class="field"><label class="lbl">{{ $t('image_from') }}</label><div class="seg"><button :aria-pressed="s.imageSource === 'activity'" @click="setSeg('imageSource', 'activity')">{{ $t('activity') }}</button><button :aria-pressed="s.imageSource === 'plan'" @click="setSeg('imageSource', 'plan')">{{ $t('drawing') }}</button><button :aria-pressed="s.imageSource === 'none'" @click="setSeg('imageSource', 'none')">{{ $t('none') }}</button></div></div>
          <div class="field">
            <div class="rowflex"><label class="lbl" style="margin:0">{{ $t('user_defines_legend') }}</label>
              <b-switch size="is-small" v-model="s.userCanDefineLegend" :disabled="s.imageSource === 'none'" />
            </div>
            <div class="hint" v-if="s.imageSource === 'none'">{{ $t('available_when_image_selected') }}</div>
          </div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('number_of_extra_images') }}</label><span class="stepper"><button @click="step('numberOfExtraImages', -1)">−</button><input type="number" v-model.number="s.numberOfExtraImages" min="0"><button @click="step('numberOfExtraImages', 1)">＋</button></span></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('user_can_hide_section') }}</label><b-switch size="is-small" v-model="s.userCanHide" /></div><div class="hint">{{ $t('user_can_hide_section_hint') }}</div></div>
        </template>

        <!-- TABLA DE TAREAS -->
        <template v-else-if="selected.type === 'tasktable'">
          <div class="field">
            <label class="lbl">{{ $t('date_columns') }} <span class="hint" style="font-weight:600">({{ $t('each_pair_start_end') }})</span></label>
            <div class="checks">
              <label class="checkpill" :class="{ on: s.schedule }"><input type="checkbox" v-model="s.schedule">Schedule Date</label>
              <label class="checkpill" :class="{ on: s.latest }"><input type="checkbox" v-model="s.latest">Latest</label>
              <label class="checkpill" :class="{ on: s.actual }"><input type="checkbox" v-model="s.actual">Actual</label>
            </div>
            <div class="hint">{{ $t('fixed_task_unit_boq') }}</div>
          </div>
          <div class="field">
            <label class="lbl">{{ $t('progress_columns') }}</label>
            <div class="checks">
              <label class="checkpill on" style="opacity:.6;cursor:default" :title="$t('fixed_column')"><input type="checkbox" checked disabled>Current progress % ({{ $t('fixed') }})</label>
              <label class="checkpill" :class="{ on: s.currentUnit }"><input type="checkbox" v-model="s.currentUnit">Current progress (unit)</label>
              <label class="checkpill" :class="{ on: s.scheduledPct }"><input type="checkbox" v-model="s.scheduledPct">Project scheduled %</label>
              <label class="checkpill" :class="{ on: s.latestPct }"><input type="checkbox" v-model="s.latestPct">Latest %</label>
            </div>
          </div>
        </template>

        <!-- GANTT -->
        <template v-else-if="selected.type === 'gantt'">
          <div class="field"><label class="lbl">{{ $t('orientation') }}</label><div class="seg"><button :aria-pressed="!s.landscape" @click="setSeg('landscape', false)">{{ $t('portrait') }}</button><button :aria-pressed="s.landscape" @click="setSeg('landscape', true)">{{ $t('landscape') }}</button></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('user_can_change_orientation') }}</label><b-switch size="is-small" v-model="s.userCanChangeOrientation" /></div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('allow_color_legend') }}</label><b-switch size="is-small" v-model="s.showColorLegend" /></div><div class="hint">{{ $t('color_legend_hint') }}</div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('number_of_gantt_images') }}</label><span class="stepper"><button @click="step('numberOfImages', -1)">−</button><input type="number" v-model.number="s.numberOfImages" min="1"><button @click="step('numberOfImages', 1)">＋</button></span></div><div class="hint">{{ $t('gantt_images_hint') }}</div></div>
          <div class="field"><div class="rowflex"><label class="lbl" style="margin:0">{{ $t('user_can_hide_section') }}</label><b-switch size="is-small" v-model="s.userCanHide" /></div><div class="hint">{{ $t('user_can_hide_section_hint') }}</div></div>
        </template>
      </div>
    </aside>

    <!-- Estado de autoguardado: badge fijo, por encima del drawer (visible al editar) -->
    <div v-if="saveState && !drawerOpen" class="save-badge" :class="saveState">
      <b-icon pack="fas" :icon="saveIcon" :custom-class="saveState === 'saving' ? 'fa-spin' : ''" size="is-small" />
      <span>{{ saveLabel }}</span>
      <span v-if="saveState === 'error'" class="status-close" title="Cerrar" @click="dismissSave"><b-icon pack="fas" icon="times" size="is-small" /></span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { TYPES, DESCRIP, DISCIPLINES, DATE_TYPES, discName, buildPreview } from './data/page-builder.render'
import { loadBuilderConfig, saveBuilderConfig } from './services/page-builder.service'
import {
  groupNames, flattenGroups, hydratePages, firstFreeDiscipline, makePage, defaultPages,
  prepareImportedPages, autoLinkDisciplines as autoLinkDisciplinesSvc, dashboardGroupIds as dashboardGroupIdsSvc,
  dashboardGroupNames as dashboardGroupNamesSvc, groupchartsExists as groupchartsExistsSvc,
  serialize as serializeSvc, getActiveTemplate, buildPalette, uploadCover,
} from './services/report-builder.service'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

export default {
  name: 'report-page-builder',

  components: { Treeselect },

  props: {
    projectId: { type: [Number, String], default: null },
    activityGroups: { type: Array, default: () => [] },
    // Config traída de OTRO proyecto (modelo builder). Si llega, el constructor arranca
    // con estas páginas (sin portada y re-enlazando grupos) en vez de la config guardada.
    importPages: { type: Array, default: null },
    // Proyecto de origen { id, name } cuando las páginas vienen de otro proyecto.
    importMeta: { type: Object, default: null },
    // Fuerza la clave de plantilla a guardar (p.ej. 'custom' al entrar por "Personalizada").
    forceTemplateKey: { type: String, default: null },
  },

  data() {
    return {
      TYPES,
      DESCRIP,
      DISCIPLINES,
      DATE_TYPES,
      uid: 1,
      pages: [],
      selectedId: null,
      paletteOpen: false,
      viewMode: 'page',
      drawerOpen: false,
      coverPreview: '', // objectURL de la imagen de portada recién elegida (no se persiste)
      s3bucket: (window.config && window.config.s3bucket) || '',
      loaded: false,    // true tras la carga inicial (evita autosave durante el hidratado)
      saveState: '',    // '' | 'saving' | 'saved' | 'error'
      templateKey: null, // plantilla en uso (weekly/executive/custom); se persiste en reports.detailed
      importedFrom: null, // { id, name } del proyecto origen si la config se copió de otro
      linkSource: {},   // pageId → 'code_custom' | 'heuristic' (origen del auto-enlace, para badge)
    }
  },

  computed: {
    ...mapGetters({ storeProject: 'app/project', storeCompany: 'app/company' }),

    selected() { return this.pages.find((p) => p.id === this.selectedId) || null },
    s() { return this.selected ? this.selected.settings : {} },

    // Grupos reales (nivel superior del árbol) o demo si aún no hay datos (servicio).
    groups() { return groupNames(this.activityGroups) },

    project() {
      return {
        name: (this.storeProject && this.storeProject.name) || 'CSF STAR DAIRY',
        company: (this.storeCompany && this.storeCompany.name) || 'x-elio',
        week: '39',
      }
    },

    ctx() { return { pages: this.pages, groups: this.groups, project: this.project, s3bucket: this.s3bucket, coverPreview: this.coverPreview, activityGroups: this.activityGroups } },

    // src para la miniatura de portada en el form: imagen elegida (objectURL) o la de BD (S3).
    coverSrc() {
      if (this.coverPreview) return this.coverPreview
      const ic = this.selected && this.selected.settings && this.selected.settings.image_cover
      return (ic && ic.url) ? ('//' + this.s3bucket + '/' + ic.url) : ''
    },

    previewHtml() { return buildPreview(this.ctx, this.viewMode, this.selectedId) },

    // Plantilla en uso (nombre + si difiere) — servicio.
    activeTemplate() { return getActiveTemplate(this.pages, this.templateKey) },

    // Árbol aplanado de grupos — servicio.
    flatGroups() { return flattenGroups(this.activityGroups) },
    // Nombres de los grupos fijos del dashboard (para la curva S) — servicio.
    dashboardGroupNames() { return dashboardGroupNamesSvc(this.pages, this.activityGroups, this.flatGroups) },
    // ¿Existe ya una página "Gráficos por grupo"? (sus curvas no se repiten en la curva S)
    groupchartsExists() { return groupchartsExistsSvc(this.pages) },

    // Badge del enlace de la disciplina seleccionada (Vinculado/Sugerido/Pendiente).
    disciplineLinkBadge() {
      if (!this.selected || this.selected.type !== 'discipline') return null
      if (this.selected.settings.groupId == null) return { cls: 'pend', txt: 'Pendiente' }
      return this.linkSource[this.selected.id] === 'heuristic'
        ? { cls: 'sug', txt: 'Sugerido — confirma' }
        : { cls: 'ok', txt: 'Vinculado' }
    },

    // Estado efectivo del toggle de curvas del dashboard (off si hay Gráficos por grupo).
    scInclude: {
      get() { return !!this.selected && this.selected.settings.includeDashboardGroups !== false && !this.groupchartsExists },
      set(v) { if (this.selected && !this.groupchartsExists) this.selected.settings.includeDashboardGroups = v },
    },

    // ¿La curva S tiene al menos un grupo que dibujar? (dashboard incluidos + extras)
    scurveHasGroups() {
      if (!this.selected || this.selected.type !== 'scurve') return false
      const s = this.selected.settings
      const dashCount = (s.includeDashboardGroups !== false && !this.groupchartsExists) ? this.dashboardGroupNames.length : 0
      return (dashCount + (s.extraGroups || []).length) > 0
    },

    // Treeselect de disciplina: deshabilita grupos ya vinculados a OTRA disciplina (1 grupo por disciplina).
    disciplineGroupNormalizer() {
      const selfId = this.selected ? this.selected.id : null
      const used = this.pages
        .filter((p) => p.type === 'discipline' && p.id !== selfId && p.settings.groupId != null)
        .map((p) => String(p.settings.groupId))
      return (node) => {
        const out = { id: node.id, label: node.label || node.name, isDisabled: used.indexOf(String(node.id)) >= 0 }
        if (node.children) out.children = node.children
        return out
      }
    },

    scurveNormalizer() {
      const on = this.scInclude // deshabilita los del dashboard solo si realmente se incluyen
      const ids = on ? this.dashboardGroupIds().map(String) : []
      return (node) => ({ id: node.id, label: node.label || node.name, children: node.children, isDisabled: ids.indexOf(String(node.id)) >= 0 })
    },

    saveIcon() { return this.saveState === 'saving' ? 'spinner' : (this.saveState === 'error' ? 'triangle-exclamation' : 'check') },
    saveLabel() { return this.saveState === 'saving' ? this.$t('saving') : (this.saveState === 'error' ? this.$t('error_saving') : this.$t('saved')) },

    // Paleta "añadir" (servicio) + nombre traducido por la vista.
    paletteItems() {
      return buildPalette(this.pages).map((it) => ({ ...it, name: this.typeLabel(it.key) }))
    },
  },

  created() {
    this.init()
  },

  watch: {
    // Autoguardado: cualquier cambio en las páginas/ajustes agenda un guardado con debounce.
    pages: {
      deep: true,
      handler() { if (this.loaded && !this._suspendSave) this.scheduleSave() },
    },
    // El árbol de grupos puede llegar después (carga async del padre): auto-enlaza al tenerlo.
    activityGroups() { this.autoLinkDisciplines() },
  },

  methods: {
    discName,

    async init() {
      const idGen = () => this.uid++
      let pages = null
      if (this.importPages && this.importPages.length) {
        // Config traída de otro proyecto: hidrata, suelta portada y limpia groupId (servicio).
        // No se persiste: el autosave escribirá cuando el usuario edite algo.
        pages = prepareImportedPages(this.importPages, idGen, this.groups)
        this.templateKey = null // config importada, no es una plantilla canónica
        this.importedFrom = this.importMeta || null // proyecto de origen (para "copiado de")
      } else if (this.projectId) {
        try {
          const { builder } = await loadBuilderConfig(this.projectId)
          if (builder && Array.isArray(builder.pages) && builder.pages.length) {
            pages = hydratePages(builder.pages, idGen, this.groups)
            this.templateKey = builder.templateKey || null
            this.importedFrom = builder.importedFrom || null
          }
        } catch (e) {
          // sin config previa o error de red: arrancamos con páginas base
        }
      }
      // Al entrar por "Personalizada" se guarda siempre como custom (config a medida).
      if (this.forceTemplateKey) this.templateKey = this.forceTemplateKey
      this.pages = (pages && pages.length) ? pages : defaultPages(idGen, this.groups)
      this.selectedId = this.pages[0] ? this.pages[0].id : null
      this.autoLinkDisciplines()
      this.$nextTick(() => { this.loaded = true })
    },

    // ---- Autoguardado ----
    scheduleSave() {
      this.saveState = 'saving'
      clearTimeout(this._saveTimer)
      this._saveTimer = setTimeout(() => this.persist(), 800)
    },

    dismissSave() { this.saveState = '' },

    async persist() {
      if (!this.projectId) return
      try {
        await this.uploadPendingCover()
        await saveBuilderConfig(this.projectId, serializeSvc(this.pages, this.templateKey, this.importedFrom))
        this.saveState = 'saved'
        clearTimeout(this._savedTimer)
        this._savedTimer = setTimeout(() => { if (this.saveState === 'saved') this.saveState = '' }, 2000)
      } catch (e) {
        this.saveState = 'error'
      }
    },

    // Sube la portada (servicio) y aplica la key bajo el guard _suspendSave (evita reencadenar
    // otro autosave por esta mutación). _suspendSave es estado de vista: guarda el watcher.
    async uploadPendingCover() {
      const r = await uploadCover(this.pages)
      if (!r) return
      this._suspendSave = true
      r.ic.type = 'file'
      r.ic.name = r.name
      r.ic.mimeType = r.mimeType
      r.ic.url = r.key
      r.ic.file = null
      if (this.coverPreview) { URL.revokeObjectURL(this.coverPreview); this.coverPreview = '' }
      this.$nextTick(() => { this._suspendSave = false })
    },

    displayName(p) {
      if (p.type === 'discipline') return p.settings.name || discName(p.settings.discipline)
      return this.typeLabel(p.type)
    },

    // Nombre traducido del tipo de página (el catálogo render.js tiene los nombres en ES).
    typeLabel(type) {
      if (type === 'gantt') return 'Gantt'
      const K = { cover: 'report_cover', dashboard: 'dashboard', exec: 'executive_summary', groupcharts: 'group_charts', scurve: 's_curve', images: 'images', discipline: 'discipline', tasktable: 'task_table' }
      return K[type] ? this.$t(K[type]) : (TYPES[type] ? TYPES[type].name : type)
    },

    // Descripción traducida del tipo de página (reemplaza DESCRIP en ES de render.js).
    typeDescLabel(type) {
      const K = { cover: 'cover_desc', dashboard: 'dashboard_desc', exec: 'exec_desc', groupcharts: 'group_charts_desc', scurve: 's_curve_desc', images: 'images_desc', discipline: 'discipline_desc', tasktable: 'tasktable_desc', gantt: 'gantt_desc' }
      return K[type] ? this.$t(K[type]) : (DESCRIP[type] || '')
    },

    addPage(key) {
      const disc = key === 'discipline' ? firstFreeDiscipline(this.pages) : undefined
      const np = makePage(key, this.uid++, this.groups, disc)
      this.pages.push(np) // nueva página siempre al final
      if (key === 'discipline') this.autoLinkDisciplines() // intenta enlazar su grupo
      this.selectedId = np.id
      this.paletteOpen = false
      this.openDrawer()
    },

    // Auto-enlace disciplina↔grupo (servicio muta groupId y devuelve el origen por página).
    autoLinkDisciplines() {
      const src = autoLinkDisciplinesSvc(this.pages, this.flatGroups)
      Object.keys(src).forEach((id) => this.$set(this.linkSource, id, src[id]))
    },

    // IDs de grupo efectivos del dashboard según su modo (servicio).
    dashboardGroupIds() { return dashboardGroupIdsSvc(this.pages, this.activityGroups) },

    selectPage(id) { this.selectedId = id; this.paletteOpen = false },

    move(id, dir) {
      const i = this.pages.findIndex((p) => p.id === id)
      const j = i + dir
      if (j < 0 || j >= this.pages.length) return
      const arr = this.pages.slice()
      const tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp
      this.pages = arr
    },

    toggleVis(id) {
      const p = this.pages.find((x) => x.id === id)
      if (p) p.visible = !p.visible
    },

    removePage(id) {
      if (this.pages.length === 1) return
      const i = this.pages.findIndex((p) => p.id === id)
      const wasSel = this.pages[i].id === this.selectedId
      this.pages.splice(i, 1)
      if (wasSel) this.selectedId = this.pages[Math.max(0, i - 1)].id
    },

    openDrawer() { if (this.selected) this.drawerOpen = true },
    closeDrawer() { this.drawerOpen = false },

    setSeg(key, val) { if (this.selected) this.selected.settings[key] = val },

    // Fechas del dashboard (1ª/2ª, no repetibles).
    setDatePair(key, val) {
      if (!this.selected) return
      const s = this.selected.settings
      s[key] = val
      if (key === 'firstDatePairType' && s.secondDatePairType === val) {
        s.secondDatePairType = ['scheduled', 'current', 'real'].filter((x) => x !== val)[0]
      }
    },

    // Normalizador del treeselect de grupos (árbol de actividades → id/label/children).
    treeNormalizer(n) {
      return { id: n.id, label: n.label || n.name, children: n.children }
    },

    step(key, delta) {
      const s = this.selected.settings
      const min = key === 'numberOfImages' ? 1 : 0
      s[key] = Math.max(min, (s[key] || 0) + delta)
    },

    disciplineOptions(p) {
      // Solo disciplinas libres + la de esta misma página.
      return DISCIPLINES.filter((d) => !this.pages.some((x) => x.type === 'discipline' && x.id !== p.id && x.settings.discipline === d[0]))
    },

    setCoverType(val) {
      if (this.selected) this.selected.settings.image_cover.type = val
    },

    onCoverFile(e) {
      const f = e.target.files && e.target.files[0]
      if (!f || !this.selected) return
      const ic = this.selected.settings.image_cover
      ic.type = 'file'
      ic.name = f.name
      ic.mimeType = f.type
      ic.file = f            // el File se sube con uppy al guardar (saveReportConfig)
      ic.url = null
      if (this.coverPreview) URL.revokeObjectURL(this.coverPreview)
      this.coverPreview = URL.createObjectURL(f)
    },

    deleteCover() {
      if (!this.selected) return
      const ic = this.selected.settings.image_cover
      ic.file = null; ic.url = null; ic.name = null; ic.mimeType = null
      if (this.coverPreview) { URL.revokeObjectURL(this.coverPreview); this.coverPreview = '' }
    },
  },
}
</script>
